import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as dates from '@simplybenefits/sb-dates';
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    TextInput,
    Button,
    Services,
    Text,
    FormModal,
    DateInput,
    Loader,
    Container,
    ContentSmall,
    Title,
    Theme,
    i18n,
    tokens,
    localizationMaps
} from '@simplybenefits/sb-ui-library';

import PersonBlock from './PersonBlock';
import { addTrustee, editTrustee, removeTrustee } from '../../actions/user';
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";


class Trustee extends PureComponent {
    state = {
        firstname: '',
        lastname: '',
        relationship: '',
        password: '',
        trustee: null,
        dialogOpen: false,
        isEdit: false,
        trusteeID: null,
        options: [ ...Services.selectFieldOptions.optionsWithSpouse(), i18n.t(tokens.labels.account.other) ],
        confirmPasswordModalOpen: false,
        trusteeToRemoveID: false,
        visibility: false,
        signage: {
            name: '',
            signature: '',
            acceptedTerms: false,
            timestampOfAcceptance: null,
            datedOn: null
        },
        skipTrustee: false
    };

    componentDidMount() {
        const { primary, contingent } = this.props;
        const beneficiaries = [ ...primary, ...contingent ];

        beneficiaries.length && beneficiaries.map((beneficiary) => {
            const age = this._getAge(beneficiary.birthdate * 1000);

            if (age >= 18) {
                this.setState({
                    skipTrustee: true
                });
            } else if (age < 18) {
                this.setState({
                    skipTrustee: false
                });
            }

            return null;
        });
    }

    _getAge = (birthdate) => {
        birthdate = dates.getUTC(birthdate);
        return Math.floor((dates.getUTC() - birthdate) / (60 * 60 * 24 * 365));
    };

    _close = () => {
        this.setState({
            firstnameError: null,
            lastnameError: null,
            relationshipError: null,
            passwordError: null,
            dialogOpen: false,
            isEdit: false,
            trusteeToRemoveID: false,
            confirmPasswordModalOpen: false,
            signage: {
                name: '',
                signature: '',
                acceptedTerms: false,
                timestampOfAcceptance: null,
                datedOn: null
            }
        });
    };

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event) => {
        this.setState({
            [type]: event.target.value
        });
    };

    _handleSignageChange = (type, value) => {
        if (type === 'acceptedTerms' && value === true) {
            this.setState({
                signage: {
                    ...this.state.signage,
                    acceptedTerms: true,
                    timestampOfAcceptance: dates.getUTC()
                }
            });

            return null;
        }

        this.setState({
            signage: {
                ...this.state.signage,
                [type]: value
            }
        });

        return null;
    };

    _validateForm = async () => {
        const firstnameError = await Services.validateInput('firstname', this.state.firstname);
        const lastnameError = await Services.validateInput('lastname', this.state.lastname);
        const relationshipError = await Services.validateInput('relationship', this.state.relationship);

        this.setState({
            firstnameError,
            lastnameError,
            relationshipError
        });

        if (
            firstnameError ||
            lastnameError ||
            relationshipError
        ) {
            return false;
        }

        return this._showConfirmPasswordDialog();
    };

    _add = () => {
        this.setState({
            firstnameError: false,
            lastnameError: false,
            sharesError: false,
            passwordError: false,
            firstname: '',
            lastname: '',
            relationship: '',
            isEdit: false,
            confirmPasswordModalOpen: false,
            trusteeToRemoveID: false,
            dialogOpen: true,
            password: '',
            signage: {
                name: '',
                signature: '',
                acceptedTerms: false,
                timestampOfAcceptance: null,
                datedOn: null
            }
        });
    };

    _edit = async (trustee) => {
        await this.setState({
            trusteeID: trustee.trusteeID,
            firstname: trustee.firstname,
            lastname: trustee.lastname,
            relationship: localizationMaps.fromEnglish("relationships", trustee.relationship),
            isEdit: true,
            dialogOpen: true,
            ageDisclaimer: false,
            trusteeToRemoveID: false,
            password: '',
            signage: {
                name: '',
                signature: '',
                acceptedTerms: false,
                timestampOfAcceptance: null,
                datedOn: null
            }
        });
    };

    _toggleVisibility = () => {
        this.setState({
            visibility: !this.state.visibility
        });
    };

    _showConfirmPasswordDialog = (trusteeToRemoveID = false) => {
        this.setState({
            dialogOpen: true,
            confirmPasswordModalOpen: true,
            trusteeToRemoveID
        });
    };

    /**
     * Called on save trustee
     * @private
     */
    _save = async () => {
        const { firstname, lastname, relationship, trusteeID, password, signage } = this.state;

        // check if the password was provided
        const passwordError = await Services.validateInput('password', password);
        this.setState({ passwordError });

        if (passwordError) {
            return false;
        }

        let success = false;

        if (this.state.isEdit) {
            success = await this.props.dispatch(editTrustee(trusteeID, {
                firstname,
                lastname,
                relationship: localizationMaps.toEnglish("relationships", relationship),
                password,
                signage: {
                    ...signage,
                    datedOn: Services.dates.formatOutgoingDate(this.state.signage.datedOn)
                }
            }));
        } else {
            success = await this.props.dispatch(addTrustee({
                firstname,
                lastname,
                relationship: localizationMaps.toEnglish("relationships", relationship),
                password,
                signage: {
                    ...signage,
                    datedOn: Services.dates.formatOutgoingDate(this.state.signage.datedOn)
                }
            }));
        }

        if (success) {
            this.setState({
                isEdit: false,
                dialogOpen: false,
                confirmPasswordModalOpen: false,
                signage: {
                    name: '',
                    signature: '',
                    acceptedTerms: false,
                    timestampOfAcceptance: null,
                    datedOn: null
                }
            });

            return true;
        }

        return false;
    };

    _validateSignageBlock = async () => {
        const { signage, password } = this.state;

        const passwordError = await Services.validateInput('checkEmpty', password);
        let signageNameError = await Services.validateInput('checkEmpty', signage.name);
        const signageSignatureError = await Services.validateInput('checkEmpty', signage.signature);
        const signageDatedOnError = await Services.validateInput('date', signage.datedOn, i18n.locale);
        const signageAcceptedError = !signage.acceptedTerms;

        if (signageAcceptedError) {
            alert(i18n.t(tokens.labels.signage.pleaseAcceptAndAcknowledgeChanges));
            return null;
        }

        if (signage.name !== signage.signature) {
            signageNameError = i18n.t(tokens.labels.signage.signageNameError);
        }

        this.setState({
            signageNameError,
            signageSignatureError,
            signageDatedOnError,
            passwordError
        });

        if (
            signageNameError ||
            signageSignatureError ||
            signageDatedOnError ||
            passwordError
        ) {
            return false;
        }

        return true;
    };

    _remove = async () => {
        const isSignageValid = await this._validateSignageBlock();

        if (!isSignageValid) {
            return null;
        }

        const signage = {
            ...this.state.signage,
            datedOn: Services.dates.formatOutgoingDate(this.state.signage.datedOn)
        };

        const success = await this.props.dispatch(removeTrustee(
            this.state.trusteeToRemoveID,
            this.state.password,
            signage
        ));

        if (success) {
            this.setState({
                trusteeToRemoveID: false,
                dialogOpen: false
            });
        }

        return null;
    };

    render() {
        const { trustee, whitelabel } = this.props;
        const styles = {
            signature: {
                fontFamily: 'AntaraDistance',
                fontWeight: 'bold',
                fontSize: 20,
                padding: 0
            },
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: "space-between"
            },
            iconStyle: {
                padding: "12px",
                color: "#414042",
                '&.MuiButtonBase-root:hover': {
                    backgroundColor: Theme.colors.hoverGrey
                }
            }
        };

        return (
            <Container>
                <ContentSmall style={{ width: '60%' }}>
                    <div style={ styles.titleBlock }>
                        <Title title={ i18n.t(tokens.titles.trustee) } containerStyle={{ flex: 1 }} />

                        {
                            (!trustee || trustee.firstname === null) && !this.state.skipTrustee &&
                            <Button
                                color="primary"
                                customStyle={{
                                    height: 50,
                                    width: 180,
                                    marginRight: 1.25,
                                    textTransform: "capitalize",
                                    fontSize: 16
                                }}
                                onClick={ this._add }
                            >
                                { i18n.t(tokens.buttons["add trustee"])}
                            </Button>
                        }
                    </div>

                    {
                        (!trustee || trustee.firstname === null) ?
                            this.state.skipTrustee ?
                                <Text
                                    testid="trustee"
                                    light
                                    medium
                                    align="center"
                                >
                                    { i18n.t(tokens.labels.account.noTrusteeRequired)}
                                </Text>
                                :
                                <Text
                                    testid="trustee"
                                    light
                                    medium
                                    align="center"
                                >
                                    { i18n.t(tokens.labels.account.addTrustee) }
                                </Text>
                            :
                            <PersonBlock
                                testid="trustee"
                                key={trustee.trusteeID}
                                firstname={trustee.firstname}
                                lastname={trustee.lastname}
                                relationship={trustee.relationship}
                                dateAdded={trustee.dateAdded}
                                parentID={trustee.parentID}
                                type="trustee"
                                edit={() => this._edit(trustee)}
                                remove={() => this._showConfirmPasswordDialog(trustee.trusteeID)}
                                iconCustomStyle={styles.iconStyle}
                            />
                    }

                    <FormModal
                        testid="add-edit-trustee-modal"
                        open={this.state.dialogOpen}
                        close={this._close}
                        title={ this.state.confirmPasswordModalOpen ? i18n.t(tokens.titles["confirm changes"]) : this.state.isEdit ? i18n.t(tokens.titles["edit trustee"]) : i18n.t(tokens.titles["add trustee"]) }
                        disableBackdropClick
                        disableEscapeKeyDown
                        customFormStyle={{ width: 420 }}
                    >
                        {
                            this.state.confirmPasswordModalOpen ?
                                <React.Fragment>
                                    <Text
                                        align="left"
                                        medium
                                        display="inline-block"
                                    >
                                        { i18n.t(tokens.labels.account.confirmTrusteeChanges) }
                                    </Text>

                                    <TextInput
                                        label={ i18n.t(tokens.labels.password) }
                                        value={this.state.password}
                                        inputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {
                                                        this.state.visibility ?
                                                            <VisibilityIcon
                                                                style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                                onClick={this._toggleVisibility}
                                                            />
                                                            :
                                                            <VisibilityOffIcon
                                                                style={{ cursor: "pointer", color: Theme.colors.darkGrey }}
                                                                onClick={this._toggleVisibility}
                                                            />
                                                    }
                                                </InputAdornment>
                                            )
                                        }}
                                        onBlur={() => this._onBlur('password')}
                                        onChange={(value) => this._handleInputChange('password', value)}
                                        error={Boolean(this.state.passwordError)}
                                        helperText={this.state.passwordError}
                                        secure={!this.state.visibility}
                                        fullWidth
                                        autofocus
                                    />

                                    { /* SIGNAGE BLOCK HERE */ }

                                    <TextInput
                                        label={ i18n.t(tokens.labels.yourName) }
                                        fullWidth
                                        value={this.state.signage.name}
                                        onChange={(e) => this._handleSignageChange('name', e.target.value)}
                                        error={ this.state.signageNameError }
                                    />

                                    <TextInput
                                        label={ i18n.t(tokens.labels.signature) }
                                        fullWidth
                                        value={this.state.signage.signature}
                                        onChange={(e) => this._handleSignageChange('signature', e.target.value)}
                                        inputProps={{ inputProps: { style: { ...styles.signature } } }}
                                        error={ this.state.signageSignatureError }
                                    />

                                    <DateInput
                                        label={ i18n.t(tokens.labels.account.datedOnYYYYMMDD) }
                                        fullWidth
                                        value={this.state.signage.datedOn}
                                        onChange={(e) => this._handleSignageChange('datedOn', e.target.value)}
                                        error={ this.state.signageDatedOnError }
                                    />

                                    <FormGroup row style={{ marginTop: 20 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={ this.state.signage.acceptedTerms }
                                                    onChange={ () => this._handleSignageChange('acceptedTerms', !this.state.signage.acceptedTerms) }
                                                    value="acceptedTerms"
                                                    color="secondary"
                                                />
                                            }
                                            label={ i18n.t(tokens.labels.account.acknowledgeInfoAccuracy)}
                                        />
                                    </FormGroup>

                                    <Button
                                        onClick={this.state.trusteeToRemoveID ? this._remove : this._save}
                                        marginTop={ 5 }
                                        color="primary"
                                        fullWidth
                                    >
                                        { i18n.t(tokens.buttons.finish) }
                                    </Button>
                                </React.Fragment>
                                :
                                <React.Fragment>

                                    <TextInput
                                        label={ i18n.t(tokens.labels.firstname) }
                                        value={this.state.firstname}
                                        onBlur={() => this._onBlur('firstname')}
                                        onChange={(value) => this._handleInputChange('firstname', value)}
                                        error={Boolean(this.state.firstnameError)}
                                        helperText={this.state.firstnameError}
                                        fullWidth
                                        autoFocus
                                    />

                                    <TextInput
                                        label={ i18n.t(tokens.labels.lastname) }
                                        value={this.state.lastname}
                                        onBlur={() => this._onBlur('lastname')}
                                        onChange={(value) => this._handleInputChange('lastname', value)}
                                        error={Boolean(this.state.lastnameError)}
                                        helperText={this.state.lastnameError}
                                        fullWidth
                                    />

                                    <TextInput
                                        label={ i18n.t(tokens.labels.relationship) }
                                        value={this.state.relationship}
                                        fullWidth
                                        onBlur={() => this._onBlur('relationship')}
                                        onChange={(value) => this._handleInputChange('relationship', value)}
                                        error={Boolean(this.state.relationshipError)}
                                        helperText={this.state.relationshipError}
                                        select
                                        selectOptions={this.state.options}
                                    />

                                    <Button
                                        onClick={this._validateForm}
                                        marginTop={ 5 }
                                        color="primary"
                                        fullWidth
                                    >
                                        { i18n.t(tokens.buttons["save trustee"])}
                                    </Button>
                                </React.Fragment>
                        }

                        {this.props.loading && <Loader whitelabel={ whitelabel } customImageStyle={{ top: '50%' }} />}
                    </FormModal>

                </ContentSmall>
            </Container>
        );
    }
}

Trustee.propTypes = {
    loading: PropTypes.bool,
    trustee: PropTypes.object,
    primary: PropTypes.array,
    contingent: PropTypes.array,
    whitelabel: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loading: state.user.loading,
    trustee: state.user.trustee,
    primary: state.user.beneficiaries.primary,
    contingent: state.user.beneficiaries.contingent,
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(Trustee);